// Pure formatting helpers for the Model A/B comparison cards (sub-plan 20). Kept framework-free so
// they are unit-testable in isolation. Every "missing" value renders as the i18n "N/A" string the
// caller passes in (never 0 / a guess) per master §8 / project no-fallback-values. No locale-dependent
// currency formatter (cost is sub-cent USD per REF-01 cost_usd NUMERIC(12,6); display to 4 dp).

import type { ComparisonRun } from './comparisonTypes';

// Cost: null -> the i18n N/A string; otherwise '$' + fixed 4 dp (do NOT round to 0 dp — costs are
// sub-cent; do NOT use Intl currency which would render 0.0001 as $0.00).
export const formatCostUsd = (v: number | null | undefined, na: string): string =>
  v === null || v === undefined ? na : `$${v.toFixed(4)}`;

// Latency: null -> N/A; otherwise seconds to 1 dp (e.g. "12.3 s").
export const formatLatency = (ms: number | null | undefined, na: string): string =>
  ms === null || ms === undefined ? na : `${(ms / 1000).toFixed(1)} s`;

// Tokens: null -> N/A; otherwise locale-grouped integer (pure display of a UTC-independent count).
export const formatTokens = (n: number | null | undefined, na: string): string =>
  n === null || n === undefined ? na : n.toLocaleString();

// Card label for a run's model. Prefer model.displayName; fall back to "provider/name" only when
// displayName is null (REF-08 §3.2 — never fabricate a model label). When all are null, returns na.
export const modelLabel = (
  model: { provider: string | null; name: string | null; displayName: string | null } | null | undefined,
  na: string,
): string => {
  if (!model) return na;
  if (model.displayName) return model.displayName;
  const { provider, name } = model;
  if (!provider && !name) return na;
  return `${provider ?? na}/${name ?? na}`;
};

// Merge the split comparison buckets into the display list (original always first), per REF-08 §3.2.
// `isOriginal` comes straight from each row's own flag (true only on `original`); we do not infer it.
export const mergeRuns = (data: {
  original: ComparisonRun;
  replays: ComparisonRun[];
}): ComparisonRun[] => [data.original, ...(data.replays ?? [])];
